import React from "react";
import {
  Container,
  Typography,
  Divider,
  Link,
  Box,
  Stack,
} from "@mui/material";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useParams } from "react-router-dom";

const Terms = () => {
  const { id } = useParams();
  const showLayout = id === "1";

  const sections = [
    {
      number: "01",
      title: "Use of Our Services",
      intro: "Our platform provides tools for:",
      items: [
        "Instagram and Facebook automation",
        "Comment and message management",
        "Marketing and engagement features",
      ],
      closing:
        "You agree to use our services only for lawful purposes and in compliance with all applicable platform policies, including Facebook and Instagram policies.",
    },
    {
      number: "02",
      title: "User Accounts",
      content:
        "To use our services, you may be required to connect your Facebook and Instagram accounts.",
      intro: "You are responsible for:",
      items: [
        "Maintaining the confidentiality of your account",
        "All activities that occur under your account",
      ],
    },
    {
      number: "03",
      title: "Data Access and Permissions",
      intro: "By connecting your account, you grant us permission to:",
      items: [
        "Access basic profile information",
        "Manage messages and comments as authorized",
        "Retrieve analytics and engagement data",
      ],
      closing:
        "We do not post or send messages without your configured automation rules.",
    },
    {
      number: "04",
      title: "Data Usage",
      intro: "We collect and use data solely to:",
      items: [
        "Provide and improve our services",
        "Automate responses and workflows",
        "Analyze engagement metrics",
      ],
      closing: "We do not sell or share your personal data with third parties.",
    },
    {
      number: "05",
      title: "Compliance with Platform Policies",
      intro: "You agree not to use our services to:",
      items: [
        "Send spam or unauthorized messages",
        "Violate Facebook or Instagram policies",
        "Engage in abusive, illegal, or harmful activities",
      ],
      closing:
        "Violation may result in suspension or termination of your account.",
    },
    {
      number: "06",
      title: "Service Availability",
      intro: "We strive to provide uninterrupted service, but we do not guarantee:",
      items: [
        "Continuous availability",
        "Error-free operation",
      ],
      closing: "We may modify or discontinue services at any time.",
    },
    {
      number: "07",
      title: "Limitation of Liability",
      intro:
        "To the extent permitted by applicable law, Hindustan Wardrobe is not responsible for:",
      items: [
        "Any loss of data",
        "Account restrictions imposed by Facebook or Instagram",
        "Damages resulting from misuse of our platform",
      ],
    },
    {
      number: "08",
      title: "Termination",
      intro: "We reserve the right to suspend or terminate access if:",
      items: [
        "Terms are violated",
        "Required by law or platform policies",
      ],
    },
    {
      number: "09",
      title: "Changes to Terms",
      content:
        "We may update these Terms at any time. Continued use of the service constitutes acceptance of the updated Terms.",
    },
  ];

  const renderItems = (items) => (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {items.map((item) => (
        <Box
          key={item}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              minWidth: 5,
              borderRadius: "50%",
              background: "#977743",
              mt: "9px",
            }}
          />

          <Typography
            sx={{
              color: "#625E58",
              fontSize: 15,
              lineHeight: 1.85,
            }}
          >
            {item}
          </Typography>
        </Box>
      ))}
    </Stack>
  );

  return (
    <>
      {showLayout && <Navbar />}

      <Box
        sx={{
          background: "#F7F4EF",
          minHeight: "100vh",
          color: "#171717",
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid #DDD7CE",
            py: { xs: 7, md: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 340,
              height: 340,
              border: "1px solid rgba(151,119,67,0.12)",
              borderRadius: "50%",
              right: { xs: -180, md: -100 },
              top: -170,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 190,
              height: 190,
              border: "1px solid rgba(151,119,67,0.1)",
              borderRadius: "50%",
              left: { xs: -110, md: -60 },
              bottom: -110,
            }}
          />

          <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "#977743",
                fontWeight: 600,
                mb: 2.5,
              }}
            >
              Hindustan Wardrobe · Legal
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontFamily: "Georgia, serif",
                fontWeight: 400,
                fontSize: {
                  xs: 46,
                  sm: 60,
                  md: 76,
                },
                lineHeight: 0.98,
                letterSpacing: "-0.03em",
                maxWidth: 850,
                mb: 3,
              }}
            >
              Terms of
              <br />
              <Box component="span" sx={{ fontStyle: "italic" }}>
                Service
              </Box>
            </Typography>

            <Typography
              sx={{
                maxWidth: 650,
                color: "#68635C",
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.8,
              }}
            >
              These terms explain the rules, responsibilities and conditions
              that apply when using Hindustan Wardrobe services.
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 3,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 1,
                  background: "#977743",
                }}
              />

              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#77716A",
                }}
              >
                Effective Date · April 10, 2023
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container
          maxWidth="md"
          sx={{
            py: { xs: 6, md: 9 },
          }}
        >
          <Box
            sx={{
              background: "#171717",
              color: "#fff",
              p: { xs: 3.5, md: 5 },
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "#C4A36A",
                mb: 2,
              }}
            >
              Service Agreement
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 24, md: 30 },
                lineHeight: 1.4,
              }}
            >
              Please review these terms carefully before connecting your
              accounts or using our services.
            </Typography>
          </Box>

          <Stack spacing={0}>
            {sections.map((section, index) => (
              <Box
                key={section.number}
                sx={{
                  pb: 5,
                  mb: 5,
                  borderBottom:
                    index === sections.length - 1
                      ? "none"
                      : "1px solid #DDD7CE",
                }}
              >
                <Stack
                  direction="row"
                  spacing={{ xs: 2, md: 3 }}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: "0.15em",
                      color: "#977743",
                      fontWeight: 600,
                      minWidth: 30,
                      pt: 0.5,
                    }}
                  >
                    {section.number}
                  </Typography>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 27, md: 32 },
                        fontWeight: 400,
                        lineHeight: 1.2,
                        mb: 2.5,
                      }}
                    >
                      {section.title}
                    </Typography>

                    {section.content && (
                      <Typography
                        sx={{
                          color: "#625E58",
                          fontSize: 15,
                          lineHeight: 1.9,
                        }}
                      >
                        {section.content}
                      </Typography>
                    )}

                    {section.intro && (
                      <Typography
                        sx={{
                          color: "#625E58",
                          fontSize: 15,
                          lineHeight: 1.85,
                          mt: section.content ? 2 : 0,
                        }}
                      >
                        {section.intro}
                      </Typography>
                    )}

                    {section.items && renderItems(section.items)}

                    {section.closing && (
                      <Typography
                        sx={{
                          color: "#625E58",
                          fontSize: 15,
                          lineHeight: 1.9,
                          mt: 2.5,
                        }}
                      >
                        {section.closing}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}

            <Box
              sx={{
                background: "#EEE9E1",
                p: { xs: 3.5, md: 5 },
                mt: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "#977743",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                10 · Contact Us
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Have questions?
              </Typography>

              <Typography
                sx={{
                  color: "#625E58",
                  fontSize: 15,
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                For any questions regarding these Terms of Service, please
                contact the Hindustan Wardrobe support team.
              </Typography>

              <Divider
                sx={{
                  borderColor: "#D5CEC3",
                  mb: 3,
                }}
              />

              <Typography
                sx={{
                  fontSize: 14,
                  color: "#292929",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Email:
                </Box>{" "}
                <Box
                  component="a"
                  href="mailto:info@hindustanwardrobe.co"
                  sx={{
                    color: "#977743",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  info@hindustanwardrobe.co
                </Box>
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {showLayout && <Footer />}
    </>
  );
};

export default Terms;